// --------------------------------------------------------------------------------------------

function getDefaultPersistentStorage() {

    if (typeof sessionStorage === 'undefined')
        return null;

    return sessionStorage;

}

function getDefaultClock() {

    return (new Date()).getTime();

}

// --------------------------------------------------------------------------------------------

class CacheSlot {

    constructor(options = {}) {

        this.name = undefined;
        this.data = undefined; // keep this undefined - used in cached slot to recover from persistent storage
        this.expires = 0;
        this.expiresIn = options.expiresIn ?? 300;
        this.loadPromise = null;
        this.clock = options.clock || getDefaultClock;

    }

    isExpired() {
        return this.expires < this.clock();
    }

    getData() {
        return this.data;
    }

    setData(data) {
        this.data = data;
        this.expires = this.clock() + this.expiresIn * 1000;
    }

    async loadData(dataLoader, forceReload = false) {

        if (typeof dataLoader !== 'function')
            throw new Error('[CacheSlot]: "dataLoader" must be a function.');

        if (this.loadPromise)
            return this.loadPromise;

        if (!forceReload && !this.isExpired())
            return this.getData();

        this.loadPromise = (async () => {
            const data = await dataLoader();
            this.setData(data);
            return data;
        })();

        try {
            return await this.loadPromise;
        } finally {
            this.loadPromise = null;
        }

    }

};

// --------------------------------------------------------------------------------------------

class PersistentCacheSlot extends CacheSlot {

    constructor(options = {}) {

        super(options);

        this.storage = options.storage || null;
        this.keyPrefix = options.keyPrefix || 'cache';

    }

    getStorageKey() {

        return `${this.keyPrefix}.${this.name}`;

    }

    isExpired() {

        return false;

    };

    readStoredData() {

        if (!this.storage)
            return null;

        const json = this.storage.getItem(this.getStorageKey());
        if (!json)
            return null;

        return JSON.parse(json);

    }

    writeStoredData(data) {

        if (!this.storage)
            return;

        const json = (data === undefined || data === null) ? null : JSON.stringify(data);
        this.storage.setItem(this.getStorageKey(), json);

    }

    getData() {

        if (this.data === undefined) {
            try {
                this.data = this.readStoredData();
            } catch {
                this.data = null;
            }
        }
        return this.data;

    };

    setData(data) {

        super.setData.call(this, data);

        try {
            this.writeStoredData(data);
        } catch {
            // Keep the in-memory value even when browser storage is unavailable or full.
        }

    };

}

// --------------------------------------------------------------------------------------------

export class Cache {

    constructor(options = {}) {

        if (typeof options === 'number')
            options = { expirationInterval: options };

        this.slots = {};
        this.expirationInterval = options.expirationInterval || 300;
        this.persistentStorage = options.persistentStorage || getDefaultPersistentStorage();
        this.keyPrefix = options.keyPrefix || 'cache';
        this.clock = options.clock || getDefaultClock;

    };

    createSlotOptions(expiresIn) {

        return {
            expiresIn,
            clock: this.clock,
        };

    }

    createPersistentSlotOptions(expiresIn) {

        return {
            ...this.createSlotOptions(expiresIn),
            storage: this.persistentStorage,
            keyPrefix: this.keyPrefix,
        };

    }

    createSlot(name, persistent = false, expiresIn = this.expirationInterval) {

        const slot = persistent
            ? new PersistentCacheSlot(this.createPersistentSlotOptions(expiresIn))
            : new CacheSlot(this.createSlotOptions(expiresIn));
        slot.name = name;

        this.slots[name] = slot;

        return slot;

    };

    getSlot(name, failIfMissing = false) {

        if (!this.slots)
            this.slots = {};

        var slot = this.slots[name];
        if (!slot && failIfMissing)
            throw new Error(`Slot "${name}" not found.`);

        return slot;

    };

    getOrCreateSlot(name, persistent = false, expiresIn = this.expirationInterval) {

        let slot = this.getSlot(name);
        if (!slot)
            slot = this.createSlot(name, persistent, expiresIn);
        return slot;

    }

    isExpired(slotName) {

        return this.getSlot(slotName).isExpired();

    };

    getData(slotName) {

        const slot = this.getSlot(slotName);
        if (!slot)
            return null;
        return slot.getData();

    };

    setData(slotName, data) {

        this.getSlot(slotName).setData(data);

    };

    async loadData(slotName, dataLoader, forceReload = false) {

        return this.getSlot(slotName, true).loadData(dataLoader, forceReload);

    };

    async loadOrCreateData(slotName, dataLoader, options = {}) {

        const {
            forceReload = false,
            persistent = false,
            expiresIn = this.expirationInterval,
        } = options;

        return this.getOrCreateSlot(slotName, persistent, expiresIn).loadData(dataLoader, forceReload);

    };

}
